import type { AxiosResponse } from "axios";
import { wooStoreApi } from "@/api/clients/wooStoreApiClient";
import type {
  AddToCartRequestPayload,
  CreateCheckoutDraftOrderRequestPayload,
  GetOrderRequestParams,
  GetProductsByCategoryRequestParams,
  GetProductListRequestParams,
  SelectShippingRatePayload,
  SubmitOrderForPostShippingRequestPayload,
  SubmitOrderForCVSShippingRequestPayload,
  UpdateAddressRequestPayload,
  UpdateCartItemRequestPayload,
} from "@/types/api/wooStoreApi";
import type { Product } from "@/types/products";
import type { ProductAttribute, ProductAttributeTerm } from "@/types/productAttribute";
import type { Cart, CartItem } from "@/types/cart";
import type { OrderData } from "@/types/checkout";
import type { OrderResult } from "@/types/order";

const wooStoreApiService = {
  product: {
    getList: async (
      params: GetProductListRequestParams | GetProductsByCategoryRequestParams,
      signal?: AbortSignal,
    ) => {
      try {
        const response = await wooStoreApi.get("/v1/products", {
          params,
          signal,
        });
        return response as AxiosResponse<Product[]>;
      } catch (error) {
        throw error;
      }
    },

    getItem: async (productSlug: string, signal?: AbortSignal) => {
      try {
        const response = await wooStoreApi.get(`/v1/products/${productSlug}`, {
          signal,
        });
        return response as AxiosResponse<Product>;
      } catch (error) {
        throw error;
      }
    },

    attributes: {
      getAll: async (signal?: AbortSignal) => {
        try {
          const response = await wooStoreApi.get("/v1/products/attributes", { signal });
          return response as AxiosResponse<ProductAttribute[]>;
        } catch (error) {
          throw error;
        }
      },

      terms: {
        getAll: async (attributeId: number, signal?: AbortSignal) => {
          try {
            const response = await wooStoreApi.get(`/v1/products/attributes/${attributeId}/terms`, {
              signal,
            });
            return response as AxiosResponse<ProductAttributeTerm[]>;
          } catch (error) {
            throw error;
          }
        },
      },
    },
  },

  cart: {
    getCartData: async (signal?: AbortSignal) => {
      try {
        const response = await wooStoreApi.get("/v1/cart", { signal });
        return response as AxiosResponse<Cart>;
      } catch (error) {
        throw error;
      }
    },

    updateCartItem: async (payload: UpdateCartItemRequestPayload) => {
      try {
        const response = await wooStoreApi.post(`/v1/cart/update-item`, payload);
        return response as AxiosResponse<Cart>;
      } catch (error) {
        throw error;
      }
    },

    removeCartItem: async (itemKey: string) => {
      try {
        const response = await wooStoreApi.delete(`/v1/cart/items/${itemKey}`);
        return response as AxiosResponse<Cart>;
      } catch (error) {
        throw error;
      }
    },

    selectShippingRate: async (payload: SelectShippingRatePayload) => {
      try {
        const response = await wooStoreApi.post("/v1/cart/select-shipping-rate", payload);
        return response as AxiosResponse<Cart>;
      } catch (error) {
        throw error;
      }
    },

    updateAddress: async (payload: UpdateAddressRequestPayload) => {
      try {
        const response = await wooStoreApi.post("/v1/cart/update-customer", payload);
        return response as AxiosResponse<Cart>;
      } catch (error) {
        throw error;
      }
    },
  },

  cartItem: {
    getItems: async (signal?: AbortSignal) => {
      try {
        const response = await wooStoreApi.get("/v1/cart/items", { signal });
        return response as AxiosResponse<CartItem[]>;
      } catch (error) {
        throw error;
      }
    },

    addToCart: async (payload: AddToCartRequestPayload) => {
      try {
        const response = await wooStoreApi.post("/v1/cart/items", payload);
        return response as AxiosResponse<CartItem>;
      } catch (error) {
        throw error;
      }
    },

    deleteAllCartItems: async () => {
      try {
        const response = await wooStoreApi.delete("/v1/cart/items");
        return response as AxiosResponse<CartItem[]>;
      } catch (error) {
        throw error;
      }
    },
  },

  checkout: {
    // 棄用
    createDraftOrder: async () => {
      try {
        const response = await wooStoreApi.get("/v1/checkout");
        return response as AxiosResponse<OrderData>;
      } catch (error) {
        throw error;
      }
    },

    // 棄用
    updateDraftOrder: async (payload: CreateCheckoutDraftOrderRequestPayload) => {
      try {
        const response = await wooStoreApi.put("/v1/checkout", payload);
        return response as AxiosResponse<OrderData>;
      } catch (error) {
        throw error;
      }
    },

    submitOrder: async (
      payload: SubmitOrderForPostShippingRequestPayload | SubmitOrderForCVSShippingRequestPayload,
    ) => {
      try {
        const response = await wooStoreApi.post("/v1/checkout", payload);
        return response as AxiosResponse<OrderData>;
      } catch (error) {
        throw error;
      }
    },
  },

  order: {
    get: async (orderId: string, params: GetOrderRequestParams, signal?: AbortSignal) => {
      try {
        const response = await wooStoreApi.get(`/v1/order/${orderId}`, {
          params,
          signal,
        });
        return response as AxiosResponse<OrderResult>;
      } catch (error) {
        throw error;
      }
    },
  },
};

export default wooStoreApiService;

